import {useMemo} from "react"
import {WatchQueryFetchPolicy} from "apollo-client"
import useAuth from "@irati/auth/helpers/auth/useAuth"
import {BudgetCategories, QueryWalletArgs, WalletEntityType} from "@irati/types"
import {createUseQuery, UseQueryExtend} from "@irati/utils/services/apollo"
import gql from "graphql-tag"
import {CurrencyType} from "@irati/payments/enums"
import walletErrorHandler from "../../services/errors/walletErrorHandler"
import budgetErrors from "../../helpers/errors/budgetErrors"
import {FullWalletFragment} from "../../graph/fragments/fullWallet"
import {Budgets} from "./shared"
import {useBudgetsPermissions} from "./useBudgetsPermissions"

const useBudgetsQuery = createUseQuery<BudgetCategories, QueryWalletArgs>(
  gql`
    query WalletBudgets($userId: String!, $currency: String!, $entityId: ID, $entityType: WalletEntityType) {
      wallet(userId: $userId, entityId: $entityId, currency: $currency, entityType: $entityType) {
        ...FullWalletFragment
      }
    }

    ${FullWalletFragment}
  `,
  {
    key: "wallet.budgets",
    errorHandlers: [walletErrorHandler, budgetErrors]
  }
)

export type UseBudgets = UseQueryExtend<typeof useBudgetsQuery, {data: Budgets}>
export type UseBudgetsProps = {userId?: string; fetchPolicy?: WatchQueryFetchPolicy}

export function useBudgetsList(props?: UseBudgetsProps): UseBudgets {
  const {user} = useAuth({})
  const uuid = props?.userId || user.uuid
  const {canBudget} = useBudgetsPermissions()
  const {data: budgets, ...apollo} = useBudgetsQuery(
    {userId: uuid!, currency: CurrencyType.RP, entityId: uuid!, entityType: WalletEntityType.Reward},
    {skip: !canBudget, fetchPolicy: props?.fetchPolicy || "cache-first"}
  )

  const data = useMemo(() => {
    if (!budgets) {
      return undefined
    }

    const total = budgets.categories.reduce((a, b) => a + b.amount, 0) + budgets.unassigned.amount
    const hasNoBudgets = budgets.categories.length === 0

    return {
      total,
      hasNoBudgets,
      hasBudgets: !hasNoBudgets,
      budgets
    }
  }, [budgets])

  return {...apollo, data}
}
